import classNames from 'classnames'
import {
  CommitmentReminderAction,
  ICommitmentReminder,
} from 'legacy/lut/services/partner360/commitment/models/ICommitmentReminder'
import React, { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'

import { RenderWhen } from 'cng-common/components/renderWhen'
import { Translation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'
import { Flex } from 'core-components/library/flex'
import { ConfirmReviewAction } from 'partner360/components/commitmentReminder/components/confirmReviewAction/ConfirmReviewAction'
import { p360TabsIds } from 'partner360/entities/partnerCompleteness'
import { PartnerCommitmentTranslations } from 'partner360/entities/partnerCompleteness'
import { isPlanSectionsDocked, requestPlanSectionDialog } from 'partner360/lib/planSectionDialog'

import { PlanReviewReminderText } from './components/planReviewReminderText/PlanReviewReminderText'

import styles from './CommitmentReminder.module.scss'

interface ICommitmentReminderProps {
  reminder: ICommitmentReminder
  onPlanReviewClick: () => void
}

const VARIANT_CONFIGS: Record<string, { className: string; icon: string }> = {
  default: {
    className: styles['root--default'],
    icon: 'kbase kbase-info-alt',
  },
  success: {
    className: styles['root--success'],
    icon: 'kbase kbase-check-circle-alt',
  },
  warning: {
    className: styles['root--warning'],
    icon: 'kbase kbase-warning-alt',
  },
  danger: {
    className: styles['root--danger'],
    icon: 'kbase kbase-close-circle-alt',
  },
  info: {
    className: styles['root--info'],
    icon: 'kbase kbase-question-alt',
  },
}

export const CommitmentReminder = ({ reminder, onPlanReviewClick }: ICommitmentReminderProps) => {
  const navigate = useNavigate()

  const onLinkClick = useCallback(
    (link: string) => () => {
      const element = document.getElementById(p360TabsIds)
      if (isPlanSectionsDocked()) {
        requestPlanSectionDialog()
      } else if (element) {
        const [rect] = element.getClientRects()

        window.scrollTo({
          top: rect.y - 150,
        })
      }
      navigate(link)
    },
    [navigate]
  )

  const { className, icon } = reminder.Variant
    ? VARIANT_CONFIGS[reminder.Variant]
    : VARIANT_CONFIGS.default

  return (
    <div key={reminder.TitleKey} className={classNames(styles.root, className || '')}>
      <div className={styles.iconWrap}>
        <span className={classNames(styles.iconWrap__icon, icon || '')} />
      </div>
      <Flex gap={'sm'} alignItems={'center'}>
        <div className={styles.body}>
          {reminder.TitleKey ? (
            <div className={styles.body__title}>
              <Translation span text={reminder.TitleKey} />
            </div>
          ) : null}

          <RenderWhen condition={reminder.Action === CommitmentReminderAction.ReviewPlanAction}>
            <PlanReviewReminderText
              text={reminder.TextKey}
              onLinkClick={onLinkClick}
              links={reminder.TextLinks}
            />
          </RenderWhen>

          <RenderWhen condition={reminder.Action !== CommitmentReminderAction.ReviewPlanAction}>
            {reminder.TextKey ? <p className={styles.body__text}>{reminder.TextKey}</p> : null}
          </RenderWhen>
        </div>
        {reminder.Link || reminder.Action === CommitmentReminderAction.ReviewPlanAction ? (
          <div className={styles.actions}>
            {reminder.Link ? (
              <Button variant={'text'} onClick={onLinkClick(reminder.Link)}>
                <Translation text={PartnerCommitmentTranslations.GoTo} />
              </Button>
            ) : null}
          </div>
        ) : null}

        <ConfirmReviewAction reminder={reminder} onPlanReviewClick={onPlanReviewClick} />
      </Flex>
    </div>
  )
}
